import * as React from 'react'
// libs
import styled from 'styled-components'
import { useMutation } from '@apollo/client/react'
// components
import { Header } from 'components/Modals/PlanItems/styles'
import { DetailsButton, DrawerModal, Scrollbar } from 'components/UI'
// view components
import AddFormFieldForm, { FormData } from './components/Form'
// hooks
import { useModalData } from 'hooks/index'
// types
import { Modals } from 'constants/index'
import { SubmissionError } from 'redux-form'
import { IField, IFieldGroupBunchResponse, IFieldGroupInput, IFieldLinkInput } from 'types/graphql'
// icons
import { CloseIcon } from 'components/UI/Icons'
// graphql
import {
  FIELD_GROUP_CREATE_MUTATION,
  FIELD_LINK_CREATE_MUTATION,
  FIELD_LINK_UPDATE_MUTATION
} from 'graphql/field/mutations'
// context
import { RiskTemplateAPIContext } from 'containers/Risks/RiskTemplate/Edit/layers/API'
// utils
import { get, omit } from 'lodash'
import { handleError } from 'utils/errors/handlers'
import { filter, map, pipe } from 'lodash/fp'
import { getValueFromOption } from 'utils/form'

const ActionsMenuWrapper = styled.div`
  position: absolute;
  top: 78px;
  height: 24px;
  width: 100%;
  right: 3px;
`

type CreateFieldResponse = {
  fieldGroupBunchCreate: IFieldGroupBunchResponse
}

const AddFormDrawer: React.FC = () => {
  const [updateFieldLink] = useMutation(FIELD_LINK_UPDATE_MUTATION)
  const [createFieldLink] = useMutation(FIELD_LINK_CREATE_MUTATION)
  const [createFieldGroup] = useMutation<CreateFieldResponse>(FIELD_GROUP_CREATE_MUTATION)

  const {
    hideModal,
    modalData: { isOpen, parentId, field, linked }
  } = useModalData<{
    field?: IField
    linked?: boolean
    parentId?: string
  }>({ modalType: Modals.RISK_TEMPLATE_ADD_FORM_MODAL })

  const { riskTemplate, linkedRiskTemplates, refetchTemplate, deleteForm } = React.useContext(RiskTemplateAPIContext)

  const fieldLink = riskTemplate.fieldLinks?.find(
    ({ field: { id }, parentFieldId }) => parentFieldId === parentId && id === field?.id
  )

  const initialValues = React.useMemo(() => {
    if (field && fieldLink) {
      const fieldGroups = riskTemplate.fieldGroups.filter(
        ({ fieldId, parentFieldId }) => fieldId === fieldLink?.field.id && parentFieldId === field.parentFieldId
      )

      const isSecondary = !fieldGroups.length

      const primaryField = riskTemplate.fieldLinks.find(
        ({ parentFieldId, primary, field: { entityId } }) =>
          parentFieldId === parentId && entityId === field.entityId && primary
      )

      const isGroupedByTemplate = fieldGroups.length && fieldGroups[0].groupedByEntityId === riskTemplate.id

      const fieldGroupBy = pipe(
        filter(({ groupedByEntityId }) => groupedByEntityId !== riskTemplate.id),
        map(({ groupedByEntityId }) => groupedByEntityId),
        (res) =>
          riskTemplate.fieldLinks
            .filter(
              ({ parentFieldId, primary, field: { entityId } }) =>
                parentFieldId === parentId && primary && res.some((id) => id === entityId)
            )
            .map(({ field: { entityId }, fieldLabel }) => ({ label: fieldLabel, value: entityId }))
      )(fieldGroups)

      const sourceTemplate = linkedRiskTemplates.find(({ fields }) => fields.some(({ id }) => id === field.id))

      return {
        groupBy: isSecondary
          ? [
              {
                value: primaryField?.field.entityId,
                label: primaryField?.fieldLabel
              }
            ]
          : fieldGroupBy,
        sourceTemplate: { value: get(sourceTemplate, 'id'), label: get(sourceTemplate, 'name') },
        primaryItem: { label: fieldLink.field.label, value: field.id },
        fieldLabel: fieldLink.fieldLabel,
        addFieldLabel: fieldLink.addFieldLabel,
        type: 'Form',
        groupByCurrent: isGroupedByTemplate,
        multiple: fieldGroupBy.length > 1,
        canLink: fieldLink.linkingSettings?.copyRecords || fieldLink.linkingSettings?.referenceRecords,
        linkingSettings: {
          ...omit(fieldLink.linkingSettings, '__typename'),
          excludeFieldsWhenCopying: fieldLink.linkingSettings?.excludeFieldsWhenCopying?.map((fieldId) => ({
            label: sourceTemplate?.fields?.find((field) => field.id === fieldId)?.label,
            value: fieldId
          }))
        },
        riskViewId: fieldLink.riskViewId
      }
    }

    return {
      type: 'Form',
      groupByCurrent: true
    }
  }, [field])

  const onFormDelete = async () => {
    await deleteForm(fieldLink)

    hideModal()
  }

  const updateField = async (data: FormData) => {
    const input = {
      fieldId: field.id,
      targetEntityId: riskTemplate.id,
      addFieldLabel: data.addFieldLabel || field.label,
      fieldLabel: data.fieldLabel || field.label,
      linkingSettings: {
        ...data.linkingSettings,
        excludeFieldsWhenCopying: data.linkingSettings?.excludeFieldsWhenCopying?.map((item) => item.value)
      },
      riskViewId: data.riskViewId
    }

    const fieldLinkId = riskTemplate.fieldLinks.find(
      ({ field: { id }, parentFieldId }) => id === field.id && parentFieldId === field.parentFieldId
    ).id

    await updateFieldLink({
      variables: { id: fieldLinkId, input }
    })

    await refetchTemplate()
  }

  const createField = async (data: FormData) => {
    let fieldGroupInput: IFieldGroupInput[]

    if (data.groupByCurrent) {
      fieldGroupInput = [
        {
          entityId: riskTemplate.id,
          fieldId: getValueFromOption(data.primaryItem),
          parentFieldId: parentId,
          groupedByEntityId: riskTemplate.id
        }
      ]
    } else if (!data.multiple) {
      fieldGroupInput = [
        {
          entityId: riskTemplate.id,
          fieldId: getValueFromOption(data.primaryItem),
          parentFieldId: parentId,
          groupedByEntityId: getValueFromOption(data.groupBy)
        }
      ]
    } else {
      fieldGroupInput = data.groupBy.map((item) => ({
        entityId: riskTemplate.id,
        fieldId: getValueFromOption(data.primaryItem),
        parentFieldId: parentId,
        groupedByEntityId: getValueFromOption(item)
      }))
    }

    const response = await createFieldGroup({
      variables: { input: fieldGroupInput }
    })

    const fieldGroupIds = response.data.fieldGroupBunchCreate.data.map(({ id }) => id)

    const fieldLinkInput: IFieldLinkInput = {
      fieldId: getValueFromOption(data.primaryItem),
      targetEntityId: riskTemplate.id,
      addFieldLabel: data.addFieldLabel || data.primaryItem.label,
      fieldLabel: data.fieldLabel || data.primaryItem.label,
      parentFieldId: parentId,
      position: riskTemplate.fields.filter((field) => field.parentFieldId === parentId).length,
      primary: true,
      linkingSettings: {
        ...data.linkingSettings,
        excludeFieldsWhenCopying: data.linkingSettings?.excludeFieldsWhenCopying?.map((item) => item.value)
      },
      riskViewId: data.riskViewId,
      fieldGroupIds
    }

    await createFieldLink({
      variables: { input: fieldLinkInput }
    })

    await refetchTemplate()
  }

  const onSubmit = async (data: FormData) => {
    if (data.canLink && !data.riskViewId) {
      throw new SubmissionError({
        _error: 'Search View configuration required'
      })
    }

    try {
      if (field) {
        await updateField(data)
      } else {
        await createField(data)
      }

      hideModal()
    } catch (e) {
      handleError(e, 'AddFormDrawer.onSubmit')
    }
  }

  return (
    <DrawerModal width='500px' isOpen={isOpen} hideModal={hideModal} data-testid='add-form-drawer'>
      <Header>
        Form
        <CloseIcon onClick={hideModal} />
      </Header>
      {field && (
        <ActionsMenuWrapper>
          <DetailsButton preset='light'>
            <span onClick={onFormDelete}>Delete</span>
          </DetailsButton>
        </ActionsMenuWrapper>
      )}
      <Scrollbar>
        <AddFormFieldForm
          field={field}
          linked={linked}
          isOpen={isOpen}
          parentId={parentId}
          onSubmit={onSubmit}
          hideModal={hideModal}
          initialValues={initialValues}
        />
      </Scrollbar>
    </DrawerModal>
  )
}

export default AddFormDrawer
